import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Plane, Car, Shield, Clock, CheckCircle, DollarSign, Phone, Mail, Search, Calendar, Users, ArrowRight, MapPin, ChevronRight, Loader2, AlertCircle, FileText, BookOpen, ScrollText, PhoneCall, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Planner from "./Planner";
import Admin from "./Admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Company Info
const COMPANY_PHONE = "1 (213) 459-5435";
const COMPANY_EMAIL = "aaditya@bmjflightdeals.net";
const FLIGHT_SERVICE_FEE = "$50";
const CAR_SERVICE_FEE = "$25";

// Header Component
const Header = () => {
  return (
    <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Plane className="h-8 w-8 text-sky-400" />
            <span className="text-2xl font-bold text-white">BMJ</span>
            <span className="text-sky-400 font-semibold">Flight Deals</span>
          </Link>
          <div className="flex items-center gap-5 text-slate-300">
            <Link to="/planner" className="hidden sm:flex items-center gap-1.5 text-sm font-medium hover:text-sky-400 transition-colors" data-testid="nav-planner-link">
              <Sparkles className="h-4 w-4 text-sky-400" /> AI Trip Planner
            </Link>
            <div className="flex items-center gap-2">
              <PhoneCall className="h-4 w-4 text-sky-400" />
              <a href={`tel:${COMPANY_PHONE}`} className="hover:text-sky-400 transition-colors font-medium">
                {COMPANY_PHONE}
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-slate-900 border-t border-slate-800 py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Plane className="h-6 w-6 text-sky-400" />
              <span className="text-xl font-bold text-white">BMJ Flight Deals</span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Your trusted partner for affordable flight bookings and car rentals.
            </p>
          </div>
          
          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Search Flights</Link></li>
              <li><Link to="/manage-booking" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Manage Booking</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Contact Us</Link></li>
            </ul>
          </div>
          
          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terms" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Refund Policy</Link></li>
              <li><Link to="/privacy" className="text-slate-400 hover:text-sky-400 transition-colors text-sm">Privacy Policy</Link></li>
            </ul>
          </div>
          
          {/* Service Fees */}
          <div>
            <h4 className="text-white font-semibold mb-4">Service Fees</h4>
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <p className="text-slate-300 text-sm mb-2">
                <span className="text-sky-400 font-medium">Flights:</span> Starting from {FLIGHT_SERVICE_FEE}
              </p>
              <p className="text-slate-300 text-sm mb-2">
                <span className="text-sky-400 font-medium">Car Rentals:</span> Starting from {CAR_SERVICE_FEE}
              </p>
              <p className="text-slate-500 text-xs mt-2">
                *Fees may vary based on itinerary complexity
              </p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-slate-800 pt-8 text-center">
          <p className="text-slate-400 text-sm">
            © 2025 BMJ International. All rights reserved.
          </p>
          <p className="text-slate-500 text-xs mt-2">
            Service fees are non-refundable and apply to all bookings.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Page Layout Component
const PageLayout = ({ children, title }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <Header />
      <main className="container mx-auto px-4 py-12">
        {title && (
          <h1 className="text-4xl font-bold text-white mb-8 text-center">{title}</h1>
        )}
        {children}
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
};

// Manage Booking Page
const ManageBooking = () => {
  const [bookingRef, setBookingRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast.info("Please call us at " + COMPANY_PHONE + " for booking assistance.");
    }, 1000);
  };

  return (
    <PageLayout title="Manage Your Booking">
      <div className="max-w-2xl mx-auto">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-sky-400" />
              Find Your Booking
            </CardTitle>
            <CardDescription className="text-slate-400">
              Enter your booking reference and email to manage your reservation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="bookingRef" className="text-slate-300">Booking Reference *</Label>
                <Input
                  id="bookingRef"
                  data-testid="input-booking-ref"
                  value={bookingRef}
                  onChange={(e) => setBookingRef(e.target.value)}
                  placeholder="e.g., BMJ123456"
                  required
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                <Input
                  id="email"
                  data-testid="input-manage-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email used for booking"
                  required
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <Button type="submit" data-testid="find-booking-btn" disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600 text-white">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                Find Booking
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white">Need Assistance?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-300">For booking changes, cancellations, or any other assistance, contact our 24/7 support:</p>
            <div className="flex flex-col gap-3">
              <a href={`tel:${COMPANY_PHONE}`} className="flex items-center gap-3 text-sky-400 hover:text-sky-300">
                <Phone className="h-5 w-5" />
                <span className="font-medium">{COMPANY_PHONE}</span>
              </a>
              <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-center gap-3 text-sky-400 hover:text-sky-300">
                <Mail className="h-5 w-5" />
                <span className="font-medium">{COMPANY_EMAIL}</span>
              </a>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <p className="text-amber-400 text-sm">
                <strong>Important:</strong> All booking modifications are subject to airline/rental company policies and may incur additional fees.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

// Terms and Conditions Page
const TermsAndConditions = () => {
  return (
    <PageLayout title="Terms and Conditions">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8">
            <p className="text-slate-400 mb-6">Last Updated: January 2025</p>
            
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-slate-300">By accessing BMJ International&apos;s services (bmjflightdeals.net), you agree to be bound by these Terms and Conditions.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Services Provided</h2>
              <p className="text-slate-300 mb-4">BMJ International acts as an intermediary between customers and travel service providers. We do not own or operate any airlines or car rental companies.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Service Fees</h2>
              <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4 mb-4">
                <p className="text-sky-300 font-medium mb-2">Our service fees:</p>
                <ul className="text-slate-300 space-y-1">
                  <li>• Flight Bookings: Starting from <strong>{FLIGHT_SERVICE_FEE}</strong></li>
                  <li>• Car Rentals: Starting from <strong>{CAR_SERVICE_FEE}</strong></li>
                  <li>• Complex itineraries may incur additional fees</li>
                </ul>
              </div>
              <p className="text-amber-400 font-medium">Service fees are NON-REFUNDABLE once the booking process has been initiated.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Booking and Payment</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>All prices are quoted in USD unless otherwise specified</li>
                <li>Full payment is required at the time of booking confirmation</li>
                <li>Prices are subject to change without notice until payment is received</li>
                <li>You are responsible for verifying all booking details before confirmation</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Cancellations and Changes</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Subject to airline/rental company policies</li>
                <li>Our service fees are non-refundable under any circumstances</li>
                <li>Processing fees: $25-$75 depending on complexity</li>
                <li>Any penalties imposed by travel providers apply</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Limitation of Liability</h2>
              <p className="text-slate-300 mb-4">BMJ International is not liable for:</p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Flight delays, cancellations, or schedule changes</li>
                <li>Baggage loss or damage</li>
                <li>Acts of God or force majeure events</li>
                <li>Government actions, visa issues, or travel restrictions</li>
              </ul>
              <p className="text-slate-300 mt-4">Maximum liability is limited to the service fees paid to BMJ International.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Travel Documentation</h2>
              <p className="text-slate-300">You are solely responsible for obtaining valid passports, visas, and other required travel documents.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">8. Dispute Resolution</h2>
              <p className="text-slate-300">Disputes shall be resolved through binding arbitration under Illinois state law. You waive any right to class action lawsuits.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">9. Contact</h2>
              <p className="text-sky-400">Phone: {COMPANY_PHONE}</p>
              <p className="text-sky-400">Email: {COMPANY_EMAIL}</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

// Refund Policy Page
const RefundPolicy = () => {
  return (
    <PageLayout title="Refund Policy">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8">
            <p className="text-slate-400 mb-6">Last Updated: January 2025</p>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-8">
              <p className="text-amber-400 font-medium">Important: Please read this policy carefully before making any booking.</p>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Service Fee Policy</h2>
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 font-bold mb-2">SERVICE FEES ARE NON-REFUNDABLE</p>
                <p className="text-slate-300">Our service fees ({FLIGHT_SERVICE_FEE} for flights, {CAR_SERVICE_FEE} for car rentals) are non-refundable under any circumstances including:</p>
                <ul className="list-disc list-inside text-slate-300 mt-2 space-y-1">
                  <li>Voluntary cancellation by the customer</li>
                  <li>Flight/rental cancellation by the provider</li>
                  <li>No-show or missed flights</li>
                  <li>Change of travel plans</li>
                  <li>Visa denial or travel restrictions</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. Flight Booking Refunds</h2>
              <p className="text-slate-300">Refunds for the actual flight ticket depend entirely on the airline&apos;s policies and fare conditions. Most discounted fares are non-refundable. Processing may take 8-12 weeks.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Cancellation Processing Fees</h2>
              <div className="bg-slate-700/50 rounded-lg p-4">
                <table className="w-full text-slate-300">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-2">Action</th>
                      <th className="text-right py-2">Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700"><td className="py-2">Cancellation processing</td><td className="text-right">$50</td></tr>
                    <tr className="border-b border-slate-700"><td className="py-2">Date/time change</td><td className="text-right">$35</td></tr>
                    <tr className="border-b border-slate-700"><td className="py-2">Name correction</td><td className="text-right">$25</td></tr>
                    <tr><td className="py-2">Complex itinerary changes</td><td className="text-right">$75+</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Travel Insurance</h2>
              <p className="text-slate-300">We strongly recommend purchasing travel insurance. BMJ International is not responsible for losses that could have been covered by insurance.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Contact for Refunds</h2>
              <p className="text-sky-400">Phone: {COMPANY_PHONE} (24/7)</p>
              <p className="text-sky-400">Email: {COMPANY_EMAIL}</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  return (
    <PageLayout title="Privacy Policy">
      <div className="max-w-4xl mx-auto">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-8">
            <p className="text-slate-400 mb-6">Last Updated: January 2025</p>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">1. Information We Collect</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li><strong>Personal Information:</strong> Name, email, phone, address</li>
                <li><strong>Travel Information:</strong> Dates, destinations, passenger details</li>
                <li><strong>Payment Information:</strong> Credit card details (processed securely)</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device information</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Processing and managing your travel bookings</li>
                <li>Communicating about your reservations</li>
                <li>Providing customer support</li>
                <li>Improving our services</li>
                <li>Sending promotional offers (with consent)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">3. Information Sharing</h2>
              <p className="text-slate-300 mb-4">We share information with:</p>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Travel providers to complete bookings</li>
                <li>Payment processors for transactions</li>
                <li>Legal authorities when required by law</li>
              </ul>
              <p className="text-slate-300 mt-4">We do not sell your information to third parties.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">4. Data Security</h2>
              <p className="text-slate-300">We use industry-standard security measures including encryption and secure servers.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">5. Your Rights</h2>
              <ul className="list-disc list-inside text-slate-300 space-y-2">
                <li>Access your personal information</li>
                <li>Request correction of data</li>
                <li>Request deletion (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">6. Data Retention</h2>
              <p className="text-slate-300">Booking records are retained for a minimum of 7 years for legal compliance.</p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">7. Contact Us</h2>
              <p className="text-sky-400">Phone: {COMPANY_PHONE}</p>
              <p className="text-sky-400">Email: {COMPANY_EMAIL}</p>
            </section>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

// Contact Page
const ContactPage = () => {
  return (
    <PageLayout title="Contact Us">
      <div className="max-w-4xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Get in Touch</CardTitle>
              <CardDescription className="text-slate-400">Our team is available 24/7</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-sky-500/20 p-3 rounded-lg"><Phone className="h-6 w-6 text-sky-400" /></div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Phone (24/7)</h3>
                  <a href={`tel:${COMPANY_PHONE}`} className="text-sky-400 hover:text-sky-300 text-lg font-medium">{COMPANY_PHONE}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-sky-500/20 p-3 rounded-lg"><Mail className="h-6 w-6 text-sky-400" /></div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Email</h3>
                  <a href={`mailto:${COMPANY_EMAIL}`} className="text-sky-400 hover:text-sky-300">{COMPANY_EMAIL}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-sky-500/20 p-3 rounded-lg"><Clock className="h-6 w-6 text-sky-400" /></div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Hours</h3>
                  <p className="text-slate-300">24 Hours / 7 Days</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Service Fees</CardTitle>
              <CardDescription className="text-slate-400">Transparent pricing</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3"><Plane className="h-5 w-5 text-sky-400" /><span className="text-white">Flights</span></div>
                  <span className="text-sky-400 font-bold">From {FLIGHT_SERVICE_FEE}</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-3"><Car className="h-5 w-5 text-sky-400" /><span className="text-white">Car Rentals</span></div>
                  <span className="text-sky-400 font-bold">From {CAR_SERVICE_FEE}</span>
                </div>
                <p className="text-slate-400 text-sm mt-4">* Fees may vary based on complexity. Non-refundable.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 mt-8">
          <CardContent className="p-6 text-center">
            <h3 className="text-white text-xl font-semibold mb-4">Ready to Book?</h3>
            <Link to="/"><Button className="bg-sky-500 hover:bg-sky-600 text-white px-8"><Search className="mr-2 h-4 w-4" />Search Flights Now</Button></Link>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

// Airport Autocomplete Component
const AirportInput = ({ value, onChange, placeholder, label }) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchAirports = useCallback(async (searchQuery) => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/airports/search`, {
        params: { query: searchQuery }
      });
      setSuggestions(response.data.airports || []);
    } catch (error) {
      console.error("Airport search error:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchAirports(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchAirports]);

  return (
    <div className="relative">
      <Label className="text-slate-300 text-sm mb-1.5 block">{label}</Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          data-testid={`airport-input-${label.toLowerCase().replace(/\s/g, '-')}`}
          value={query}
          onChange={(e) => {
            const newValue = e.target.value;
            setQuery(newValue);
            onChange(newValue);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={placeholder}
          className="pl-10 bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-sky-500 focus:ring-sky-500/20"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {suggestions.map((airport, index) => (
            <button
              key={index}
              data-testid={`airport-suggestion-${airport.iata}`}
              onClick={() => {
                setQuery(airport.iata);
                onChange(airport.iata);
                setShowSuggestions(false);
              }}
              className="w-full px-4 py-3 text-left hover:bg-slate-700 flex items-center gap-3 border-b border-slate-700 last:border-b-0 transition-colors"
            >
              <div className="bg-sky-500/20 p-2 rounded">
                <Plane className="h-4 w-4 text-sky-400" />
              </div>
              <div>
                <p className="text-white font-medium">{airport.iata} - {airport.city}</p>
                <p className="text-slate-400 text-sm">{airport.name}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Date Picker Component
const DatePicker = ({ value, onChange, label, placeholder }) => {
  const [open, setOpen] = useState(false);
  
  const handleSelect = (date) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : "";
    onChange(formattedDate);
    setOpen(false);
  };
  
  return (
    <div>
      <Label className="text-slate-300 text-sm mb-1.5 block">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            data-testid={`date-picker-${label.toLowerCase().replace(/\s/g, '-')}`}
            variant="outline"
            className="w-full justify-start text-left font-normal bg-slate-800/50 border-slate-600 text-white hover:bg-slate-700 hover:text-white"
          >
            <Calendar className="mr-2 h-4 w-4 text-slate-400" />
            {value ? format(new Date(value), "PPP") : <span className="text-slate-500">{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-600" align="start">
          <CalendarComponent
            mode="single"
            selected={value ? new Date(value) : undefined}
            onSelect={handleSelect}
            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
            initialFocus
            className="bg-slate-800 text-white"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Flight Card Component
const FlightCard = ({ flight, onSelect }) => {
  const formatTime = (dateStr) => {
    if (!dateStr) return "TBD";
    try {
      return format(new Date(dateStr), "HH:mm");
    } catch {
      return dateStr.split("T")[1]?.substring(0, 5) || "TBD";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return format(new Date(dateStr), "MMM dd");
    } catch {
      return dateStr.split("T")[0] || "";
    }
  };

  return (
    <Card 
      data-testid={`flight-card-${flight.flight_number}`}
      className="bg-slate-800/50 border-slate-700 hover:border-sky-500/50 transition-all duration-300 cursor-pointer group"
      onClick={() => onSelect(flight)}
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-sky-500/20 p-2 rounded-lg">
              <Plane className="h-5 w-5 text-sky-400" />
            </div>
            <div>
              <p className="text-white font-semibold">{flight.airline}</p>
              <p className="text-slate-400 text-sm">{flight.flight_number}</p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            flight.status === 'scheduled' ? 'bg-green-500/20 text-green-400' :
            flight.status === 'active' ? 'bg-sky-500/20 text-sky-400' :
            flight.status === 'landed' ? 'bg-slate-500/20 text-slate-400' :
            'bg-amber-500/20 text-amber-400'
          }`}>
            {flight.status}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{formatTime(flight.departure?.scheduled)}</p>
            <p className="text-sky-400 font-medium">{flight.departure?.iata}</p>
            <p className="text-slate-400 text-sm">{formatDate(flight.departure?.scheduled)}</p>
          </div>
          
          <div className="flex-1 mx-6 relative">
            <div className="border-t-2 border-dashed border-slate-600"></div>
            <Plane className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 text-sky-400 rotate-90" />
          </div>
          
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{formatTime(flight.arrival?.scheduled)}</p>
            <p className="text-sky-400 font-medium">{flight.arrival?.iata}</p>
            <p className="text-slate-400 text-sm">{formatDate(flight.arrival?.scheduled)}</p>
          </div>
        </div>
        
        {/* Pricing Section */}
        <div className="mt-4 pt-4 border-t border-slate-700">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-slate-400 text-xs uppercase tracking-wide">Flight Price</p>
              <p className="text-white font-medium">Request Quote</p>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs uppercase tracking-wide">+ Service Fee</p>
              <p className="text-sky-400 font-bold text-lg">{FLIGHT_SERVICE_FEE}</p>
            </div>
          </div>
          <div className="bg-slate-700/30 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <span className="text-slate-300 text-sm">Estimated Total</span>
              <span className="text-amber-400 font-medium">Flight Price + {FLIGHT_SERVICE_FEE} Service Fee</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-slate-400 text-sm">
            {flight.departure?.terminal && <span>Terminal {flight.departure.terminal}</span>}
            {flight.departure?.gate && <span> • Gate {flight.departure.gate}</span>}
          </div>
          <Button 
            data-testid={`select-flight-${flight.flight_number}`}
            variant="default" 
            className="bg-sky-500 hover:bg-sky-600 text-white"
          >
            Get Price Quote <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Contact Form Component
const ContactForm = ({ searchParams, selectedFlight, onBack }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const payload = {
        ...formData,
        departure_airport: searchParams.from,
        arrival_airport: searchParams.to,
        departure_date: searchParams.date,
        return_date: searchParams.returnDate || null,
        passengers: searchParams.passengers,
        selected_flight: selectedFlight ? `${selectedFlight.airline} ${selectedFlight.flight_number}` : null
      };
      
      await axios.post(`${API}/leads/submit`, payload);
      setSubmitted(true);
      toast.success("Enquiry submitted successfully! We'll contact you shortly.");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="bg-green-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="h-10 w-10 text-green-400" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4">Thank You!</h2>
        <p className="text-slate-400 text-lg mb-8">
          Your enquiry has been submitted successfully.<br />
          Our team will contact you within 24 hours.
        </p>
        <Button 
          data-testid="search-again-btn"
          onClick={onBack} 
          className="bg-sky-500 hover:bg-sky-600 text-white"
        >
          Search Another Flight
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-2">Complete Your Booking Request</h2>
        <p className="text-slate-400">Fill in your details and we&apos;ll find you the best deal</p>
      </div>
      
      {/* Trip Summary */}
      <Card className="bg-slate-800/50 border-slate-700 mb-8">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Plane className="h-5 w-5 text-sky-400" />
            Trip Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-slate-300">
            <div>
              <p className="text-slate-500 text-sm">From</p>
              <p className="font-medium">{searchParams.from}</p>
            </div>
            <div>
              <p className="text-slate-500 text-sm">To</p>
              <p className="font-medium">{searchParams.to}</p>
            </div>
            <div>
              <p className="text-slate-500 text-sm">Departure</p>
              <p className="font-medium">{searchParams.date}</p>
            </div>
            <div>
              <p className="text-slate-500 text-sm">Return</p>
              <p className="font-medium">{searchParams.returnDate || "One Way"}</p>
            </div>
            <div>
              <p className="text-slate-500 text-sm">Passengers</p>
              <p className="font-medium">{searchParams.passengers}</p>
            </div>
            {selectedFlight && (
              <div>
                <p className="text-slate-500 text-sm">Selected Flight</p>
                <p className="font-medium text-sky-400">{selectedFlight.airline} {selectedFlight.flight_number}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Contact Form */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Your Contact Information</CardTitle>
          <CardDescription className="text-slate-400">
            We&apos;ll use this information to send you the best flight deals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="full_name" className="text-slate-300">Full Name *</Label>
              <Input
                id="full_name"
                data-testid="input-full-name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Doe"
                required
                className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                <Input
                  id="email"
                  data-testid="input-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="input-phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  required
                  className="bg-slate-800/50 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="message" className="text-slate-300">Additional Message (Optional)</Label>
              <textarea
                id="message"
                data-testid="input-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Any special requirements or preferences..."
                rows={4}
                className="w-full px-3 py-2 bg-slate-800/50 border border-slate-600 rounded-md text-white placeholder:text-slate-500 focus:border-sky-500 focus:ring-1 focus:ring-sky-500/20"
              />
            </div>
            
            <div className="flex gap-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onBack}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                Back to Results
              </Button>
              <Button 
                type="submit" 
                data-testid="submit-enquiry-btn"
                disabled={submitting}
                className="flex-1 bg-sky-500 hover:bg-sky-600 text-white"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Enquiry
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Home Component
const Home = () => {
  const [activeTab, setActiveTab] = useState("flights");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [passengers, setPassengers] = useState("1");
  const [searching, setSearching] = useState(false);
  const [flights, setFlights] = useState([]);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearching(true);
    setError(null);
    setFlights([]);
    
    try {
      const response = await axios.post(`${API}/flights/search`, {
        departure_airport: from,
        arrival_airport: to,
        departure_date: departureDate,
        return_date: returnDate || null,
        passengers: parseInt(passengers)
      });
      
      setFlights(response.data.flights || []);
      setSearchParams({
        from,
        to,
        date: departureDate,
        returnDate: returnDate || null,
        passengers: parseInt(passengers)
      });
      
      if (response.data.flights?.length === 0) {
        toast.info("No flights found for the selected route and date. Try different dates.");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError(err.response?.data?.detail || "Failed to search flights. Please try again.");
      toast.error("Failed to search flights");
    } finally {
      setSearching(false);
    }
  };

  const handleFlightSelect = (flight) => {
    setSelectedFlight(flight);
    setShowContactForm(true);
  };

  const resetSearch = () => {
    setShowContactForm(false);
    setSelectedFlight(null);
    setFlights([]);
    setSearchParams(null);
    setFrom("");
    setTo("");
    setDepartureDate("");
    setReturnDate("");
    setPassengers("1");
  };

  // Show contact form
  if (showContactForm && searchParams) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <ContactForm 
            searchParams={searchParams} 
            selectedFlight={selectedFlight}
            onBack={() => setShowContactForm(false)}
          />
        </main>
        <Footer />
        <Toaster position="top-right" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1920')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Lowest Prices on <span className="text-sky-400">Flights</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Search real-time flight schedules and get the best deals. Book with confidence!
            </p>
          </div>

          {/* Search Box */}
          <Card className="bg-slate-800/80 backdrop-blur-lg border-slate-700 max-w-5xl mx-auto shadow-2xl">
            <CardContent className="p-6">
              {/* Tabs */}
              <div className="flex gap-4 mb-6">
                <Button
                  data-testid="tab-flights"
                  variant={activeTab === "flights" ? "default" : "ghost"}
                  onClick={() => setActiveTab("flights")}
                  className={activeTab === "flights" ? "bg-sky-500 hover:bg-sky-600" : "text-slate-300 hover:text-white"}
                >
                  <Plane className="mr-2 h-4 w-4" />
                  Flights
                </Button>
                <Button
                  data-testid="tab-car-rentals"
                  variant={activeTab === "cars" ? "default" : "ghost"}
                  onClick={() => setActiveTab("cars")}
                  className={activeTab === "cars" ? "bg-sky-500 hover:bg-sky-600" : "text-slate-300 hover:text-white"}
                >
                  <Car className="mr-2 h-4 w-4" />
                  Car Rentals
                </Button>
              </div>

              {/* Search Form */}
              <form onSubmit={handleSearch} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <AirportInput
                    value={from}
                    onChange={setFrom}
                    placeholder="City or Airport"
                    label="From"
                  />
                  <AirportInput
                    value={to}
                    onChange={setTo}
                    placeholder="City or Airport"
                    label="To"
                  />
                  <DatePicker
                    value={departureDate}
                    onChange={setDepartureDate}
                    label="Departure Date"
                    placeholder="Select date"
                  />
                  <DatePicker
                    value={returnDate}
                    onChange={setReturnDate}
                    label="Return Date (Optional)"
                    placeholder="Select date"
                  />
                </div>

                <div className="flex flex-col md:flex-row gap-4 items-end">
                  <div className="w-full md:w-48">
                    <Label className="text-slate-300 text-sm mb-1.5 block">Passengers</Label>
                    <Select value={passengers} onValueChange={setPassengers}>
                      <SelectTrigger 
                        data-testid="select-passengers"
                        className="bg-slate-800/50 border-slate-600 text-white"
                      >
                        <Users className="mr-2 h-4 w-4 text-slate-400" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-600">
                        {[1, 2, 3, 4, 5, 6].map((num) => (
                          <SelectItem key={num} value={num.toString()} className="text-white hover:bg-slate-700">
                            {num} Passenger{num > 1 ? "s" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    data-testid="search-flights-btn"
                    disabled={searching || !from || !to || !departureDate}
                    className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white h-10 text-lg shadow-lg shadow-sky-500/25"
                  >
                    {searching ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-5 w-5" />
                        Search Flights
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* AI Trip Planner Banner */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto rounded-2xl border border-sky-500/30 bg-gradient-to-r from-sky-500/10 to-blue-600/10 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="bg-sky-500/20 rounded-xl p-3"><Sparkles className="h-7 w-7 text-sky-400" /></div>
              <div>
                <h2 className="text-2xl font-bold text-white">Plan Your Trip in Minutes with AI</h2>
                <p className="text-slate-300 mt-1 max-w-xl">Get a personalised day-wise sightseeing itinerary for any destination. Quick, smart and tailored to your interests.</p>
              </div>
            </div>
            <Link to="/planner" data-testid="hero-planner-cta">
              <Button className="bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white h-12 px-8 text-base shadow-lg shadow-sky-500/25 whitespace-nowrap">
                Create My Itinerary <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {error && (
        <section className="container mx-auto px-4 py-4">
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-center gap-3 max-w-5xl mx-auto">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-red-300">{error}</p>
          </div>
        </section>
      )}

      {/* Flight Results */}
      {flights.length > 0 && (
        <section className="container mx-auto px-4 py-12" data-testid="flight-results">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  Available Flights: {searchParams?.from} → {searchParams?.to}
                </h2>
                <p className="text-slate-400">{flights.length} flights found for {searchParams?.date}</p>
              </div>
              <Button
                data-testid="proceed-without-selection-btn"
                onClick={() => setShowContactForm(true)}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                Get Quote Without Selection
              </Button>
            </div>

            {/* Pricing Notice Banner */}
            <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <DollarSign className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sky-300 font-medium mb-1">Pricing Information</p>
                  <p className="text-slate-300 text-sm">
                    Flight prices vary based on availability and booking date. Select a flight to request a personalized quote. 
                    <span className="text-sky-400 font-medium"> Service fee of {FLIGHT_SERVICE_FEE}</span> applies to all bookings.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              {flights.map((flight, index) => (
                <FlightCard
                  key={flight.id || index}
                  flight={flight}
                  onSelect={handleFlightSelect}
                />
              ))}
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-slate-400 mb-4">Can&apos;t find what you&apos;re looking for?</p>
              <Button
                data-testid="contact-without-flight-btn"
                onClick={() => setShowContactForm(true)}
                variant="outline"
                className="border-sky-500 text-sky-400 hover:bg-sky-500/10"
              >
                Contact Us for Custom Quote
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {flights.length === 0 && !searching && (
        <>
          <section className="container mx-auto px-4 py-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { icon: Shield, title: "Best Price Guarantee", desc: "Lowest available prices" },
                { icon: Clock, title: "24/7 Support", desc: "Always here to help" },
                { icon: CheckCircle, title: "Instant Confirmation", desc: "Book in seconds" },
                { icon: DollarSign, title: "No Hidden Fees", desc: "Transparent pricing" }
              ].map((feature, idx) => (
                <div key={idx} className="text-center p-6 rounded-xl bg-slate-800/50 border border-slate-700 hover:border-sky-500/50 transition-colors">
                  <feature.icon className="h-10 w-10 text-sky-400 mx-auto mb-4" />
                  <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                  <p className="text-slate-400 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Why Choose Section */}
          <section className="bg-slate-800/50 py-16">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold text-white text-center mb-12">
                Why Choose <span className="text-sky-400">BMJ International</span>?
              </h2>
              <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                {[
                  {
                    title: "Compare & Save",
                    desc: "Search real-time flight schedules from multiple airlines. We find you the best available routes and times."
                  },
                  {
                    title: "Quick & Easy Booking",
                    desc: "Simple enquiry process. Submit your travel details and our team will get you the best prices."
                  },
                  {
                    title: "24/7 Customer Care",
                    desc: "Our dedicated support team is available around the clock to assist with your travel needs."
                  }
                ].map((item, idx) => (
                  <Card key={idx} className="bg-slate-900/50 border-slate-700">
                    <CardHeader>
                      <CardTitle className="text-white">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-400">{item.desc}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Pricing Transparency Section */}
          <section className="container mx-auto px-4 py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-white mb-4">
                  Transparent <span className="text-sky-400">Pricing</span>
                </h2>
                <p className="text-slate-300 max-w-2xl mx-auto">
                  We believe in complete transparency. Here are our service fees - no surprises, no hidden charges.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                {/* Flight Booking Fees */}
                <Card className="bg-slate-800/50 border-slate-700 hover:border-sky-500/50 transition-colors">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-sky-500/20 p-3 rounded-lg">
                        <Plane className="h-6 w-6 text-sky-400" />
                      </div>
                      <div>
                        <CardTitle className="text-white">Flight Bookings</CardTitle>
                        <CardDescription className="text-slate-400">Domestic & International</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Service Fee</span>
                        <span className="text-sky-400 font-bold text-xl">From {FLIGHT_SERVICE_FEE}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Date/Time Change</span>
                        <span className="text-white font-medium">$35</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Name Correction</span>
                        <span className="text-white font-medium">$25</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-slate-300">Cancellation Processing</span>
                        <span className="text-white font-medium">$50</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Car Rental Fees */}
                <Card className="bg-slate-800/50 border-slate-700 hover:border-sky-500/50 transition-colors">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-sky-500/20 p-3 rounded-lg">
                        <Car className="h-6 w-6 text-sky-400" />
                      </div>
                      <div>
                        <CardTitle className="text-white">Car Rentals</CardTitle>
                        <CardDescription className="text-slate-400">All Locations</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Service Fee</span>
                        <span className="text-sky-400 font-bold text-xl">From {CAR_SERVICE_FEE}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Date/Time Change</span>
                        <span className="text-white font-medium">$20</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-slate-700">
                        <span className="text-slate-300">Location Change</span>
                        <span className="text-white font-medium">$25</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-slate-300">Cancellation Processing</span>
                        <span className="text-white font-medium">$35</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Important Disclosures */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-amber-500/20 p-2 rounded-lg flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-amber-400 font-semibold mb-2">Important Service Fee Disclosure</h4>
                    <ul className="text-slate-300 text-sm space-y-2">
                      <li>• <strong>Service fees are NON-REFUNDABLE</strong> once the booking process has been initiated</li>
                      <li>• Fees may be higher for complex itineraries (multi-city, group bookings, special requests)</li>
                      <li>• Airline/rental company penalties and fare differences are additional and vary by provider</li>
                      <li>• All prices are in USD and subject to change without notice</li>
                    </ul>
                    <div className="mt-4">
                      <Link to="/refund-policy" className="text-amber-400 hover:text-amber-300 text-sm font-medium inline-flex items-center gap-1">
                        Read Full Refund Policy <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact for Custom Quote */}
              <div className="mt-8 text-center">
                <p className="text-slate-400 mb-4">Need a quote for a complex itinerary?</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a href={`tel:${COMPANY_PHONE}`} className="flex items-center gap-2 text-sky-400 hover:text-sky-300 font-medium">
                    <Phone className="h-5 w-5" />
                    {COMPANY_PHONE}
                  </a>
                  <span className="text-slate-600 hidden sm:inline">|</span>
                  <a href={`mailto:${COMPANY_EMAIL}`} className="flex items-center gap-2 text-sky-400 hover:text-sky-300 font-medium">
                    <Mail className="h-5 w-5" />
                    {COMPANY_EMAIL}
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="container mx-auto px-4 py-16">
            <div className="bg-gradient-to-r from-sky-500/20 to-blue-600/20 rounded-2xl p-12 text-center border border-sky-500/30">
              <h2 className="text-3xl font-bold text-white mb-4">Ready to Save on Your Next Trip?</h2>
              <p className="text-slate-300 mb-8">Join thousands of satisfied customers who trust BMJ International</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  data-testid="cta-search-flights"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-sky-500 hover:bg-sky-600 text-white px-8"
                >
                  Search Flights Now
                </Button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Footer */}
      <Footer />
      
      <Toaster position="top-right" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/manage-booking" element={<ManageBooking />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/planner" element={<Planner />} />
          <Route path="/planner/:id" element={<Planner />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
